"use client";

import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Icon, IconName } from "~/components/icons/Icon";
import { colors } from "~/utils/tailwindUtils";

export type DropdownOption =
  | {
      readonly value: string;
      readonly label: string;
    }
  | {
      value: string;
      label: string;
    };

type DropdownProps = {
  options: readonly DropdownOption[] | DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  name?: string;
  placeholder?: string | React.ReactNode;
  isDisabled?: boolean;
  classNames?: string;
  error?: boolean;
  openUpward?: boolean;
  hideIcon?: boolean;
  customMenuContent?: React.ReactNode;
  useRelativePositioning?: boolean; // If true, use relative positioning instead of fixed (for modals)
};

/**
 * Dropdown component that allows the user to select from a list of options
 * @param options - Array of options with value and label
 * @param value - Selected value
 * @param onChange - Callback when value changes
 * @param name - Input name attribute
 * @param placeholder - Placeholder text when no value is selected
 * @param isDisabled - Whether the dropdown is disabled
 * @param classNames - Additional CSS classes
 * @param error - Whether to show error styling
 * @param openUpward - Whether to open the dropdown upward instead of downward
 * @returns Dropdown component
 */
const Dropdown = ({ options, value, onChange, placeholder, isDisabled, classNames, error, openUpward = false, hideIcon = false, customMenuContent, useRelativePositioning = false }: DropdownProps) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  const selectedOption = options.find((option) => option.value === value);

  const handleOptionClick = (optionValue: string) => {
    onChange(optionValue);
    setDropdownOpen(false);
  };

  const handleClickOutside = (e: MouseEvent) => {
    // Use setTimeout to delay closing the dropdown, otherwise it will close immediately
    setTimeout(() => {
      const target = e.target as Node;
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(target) &&
        dropdownMenuRef.current &&
        !dropdownMenuRef.current.contains(target)
      ) {
        setDropdownOpen(false);
      }
    }, 250);
  };

  // Calculate dropdown position when it opens
  useEffect(() => {
    if (dropdownOpen && dropdownRef.current) {
      if (useRelativePositioning) {
        // For relative positioning, we don't need to calculate absolute position
        // The dropdown will position itself relative to the container
        setDropdownPosition({
          top: 0,
          left: 0,
          width: dropdownRef.current.offsetWidth,
        });
      } else {
        // For fixed positioning, calculate absolute position
        const rect = dropdownRef.current.getBoundingClientRect();
        // When openUpward: position top at trigger top, then translateY(-100%) moves menu above
        setDropdownPosition({
          top: openUpward ? rect.top - 4 : rect.bottom + 4,
          left: rect.left,
          width: rect.width,
        });
      }
    } else {
      setDropdownPosition(null);
    }
  }, [dropdownOpen, openUpward, useRelativePositioning]);

  // Close dropdown when clicked outside the component
  useEffect(() => {
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [dropdownOpen]);

  // Check if placeholder is a ReactNode (custom trigger)
  const isCustomTrigger = placeholder !== undefined && typeof placeholder !== "string";

  return (
    <div className={twMerge("w-full", useRelativePositioning && "relative", classNames)} ref={dropdownRef}>
      {/* Display selected option */}
      <div
        className={twMerge(
          isCustomTrigger
            ? "flex items-center justify-center rounded-lg p-1.5 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors cursor-pointer"
            : "flex items-center justify-between bg-gray-50 text-black text-sm rounded-lg px-3 py-2 cursor-pointer h-10 hover:bg-gray-100 border border-gray-300 gap-2",
          error && "border-red-500",
          isDisabled && "opacity-50 cursor-not-allowed"
        )}
        onClick={() => !isDisabled && setDropdownOpen(!dropdownOpen)}
      >
        {isCustomTrigger ? (
          <span className="flex-1 flex items-center justify-center">{placeholder}</span>
        ) : (
          <span
            className={twMerge(
              "font-medium text-black md:text-base truncate min-w-0 flex-1",
              !selectedOption && "text-gray-400"
            )}
          >
            {selectedOption ? selectedOption.label : (typeof placeholder === "string" ? placeholder : "Select an option")}
          </span>
        )}
        {!hideIcon && (
          <Icon
            name={IconName.ChevronDown}
            color={colors.gray[600]}
            width={24}
            height={24}
            className={twMerge("transition-transform duration-200 shrink-0", dropdownOpen && "rotate-180")}
          />
        )}
      </div>

      {/* Dropdown options */}
      {dropdownOpen && !isDisabled && dropdownPosition && (
        <div
          ref={dropdownMenuRef}
          className={twMerge(
            "z-100 bg-white rounded-xl shadow-lg",
            customMenuContent ? "border border-gray-100" : "border border-gray-300",
            "max-h-60 overflow-auto",
            useRelativePositioning 
              ? (openUpward ? "absolute bottom-full mb-2" : "absolute mt-2")
              : "fixed",
            // For custom menu content, don't force width - let it control its own width
            // For regular options, use full width when relative, or calculated width when fixed
            customMenuContent 
              ? (useRelativePositioning ? "" : "")
              : (useRelativePositioning ? "w-full" : "")
          )}
          style={useRelativePositioning 
            ? undefined 
            : {
                top: `${dropdownPosition.top}px`,
                // For custom menu content, right-align the dropdown to the trigger
                // For regular options, align to left edge
                left: customMenuContent 
                  ? `${dropdownPosition.left + dropdownPosition.width}px`
                  : `${dropdownPosition.left}px`,
                // Only set width for fixed positioning when NOT using customMenuContent
                // Custom menu content should control its own width
                ...(customMenuContent ? {} : { width: `${dropdownPosition.width}px` }),
                // For custom menu content, use transform to right-align
                // For openUpward, translateY(-100%) positions menu above the trigger
                ...(customMenuContent
                  ? { transform: "translateX(-100%)" }
                  : openUpward
                    ? { transform: "translateY(-100%)" }
                    : {}),
              }}
          onMouseDown={(e) => {
            // Prevent blur when clicking on dropdown
            e.preventDefault();
          }}
        >
          {customMenuContent ? (
            customMenuContent
          ) : (
            <ul>
              {options.map((option) => (
                <li
                  key={option.value}
                  onClick={() => handleOptionClick(option.value)}
                  className={twMerge(
                    "flex items-center p-3 cursor-pointer hover:bg-gray-100 text-sm font-medium",
                    option.value === value && "bg-gray-50"
                  )}
                >
                  <span className="text-black">{option.label}</span>
                  {option.value === value && (
                    <Icon name={IconName.Check} color={colors.green[600]} width={20} height={20} className="ml-auto" />
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
